import { getBackendUrl } from '../config';
import moment from 'moment';

/**
 * Hilfsfunktion zur Generierung einer vergangenen Zeitspanne am heutigen Tag.
 * Liefert das passende Wochentagskürzel, Start-/Endzeit und das Datumsformat.
 */
function getPastTimeToday() {
  const now = new Date();
  
  // Get Europe/Berlin local time components using Intl formatter
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false
  });
  
  const parts = formatter.formatToParts(now);
  const getPart = (type) => parts.find(p => p.type === type).value;
  
  const year = parseInt(getPart('year'), 10);
  const month = parseInt(getPart('month'), 10);
  const day = parseInt(getPart('day'), 10);
  const hour = parseInt(getPart('hour'), 10);
  const minute = parseInt(getPart('minute'), 10);
  
  // Construct a Date representing Berlin local time to get correct day of week
  const berlinDate = new Date(year, month - 1, day, hour, minute);
  
  let startHour = 0;
  let endHour = 0;
  let startMinute = 0;
  let endMinute = 0;
  
  if (hour >= 3) {
    // Sicher in der Vergangenheit am selben Tag
    startHour = hour - 3;
    endHour = hour - 2;
  } else if (hour >= 1) {
    startHour = 0;
    endHour = 0;
    endMinute = 30;
  } else {
    startHour = 0;
    endHour = 0;
    startMinute = 0;
    endMinute = Math.max(1, minute - 5);
  }
  
  const pad = (num) => String(num).padStart(2, '0');
  
  const wochentage = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  const wochentag = wochentage[berlinDate.getDay()];
  
  const startZeit = `${pad(startHour)}:${pad(startMinute)}`;
  const endeZeit = `${pad(endHour)}:${pad(endMinute)}`;
  const dateStr = `${year}-${pad(month)}-${pad(day)}`;
  
  const duration = (endHour * 60 + endMinute) - (startHour * 60 + startMinute);
  
  // Get Europe/Berlin offset dynamically
  const tzString = now.toLocaleString('en-US', { timeZone: 'Europe/Berlin', timeZoneName: 'longOffset' });
  const match = tzString.match(/GMT([+-]\d+)/);
  const offsetHours = match ? parseInt(match[1], 10) : 2;
  const offsetSign = offsetHours >= 0 ? '+' : '-';
  const offsetStr = `${offsetSign}${pad(Math.abs(offsetHours))}:00`;
  
  return {
    wochentag,
    startZeit,
    endeZeit,
    dateStr,
    duration: duration > 0 ? duration : 45,
    offsetStr
  };
}

/**
 * Hilfsfunktion zum Auslesen detaillierter API-Fehlermeldungen.
 */
async function getErrorMessage(response, defaultMsg) {
  try {
    const data = await response.json();
    return data.message || data.error || `${defaultMsg} (HTTP ${response.status})`;
  } catch {
    try {
      const text = await response.text();
      return text || `${defaultMsg} (HTTP ${response.status})`;
    } catch {
      return `${defaultMsg} (HTTP ${response.status})`;
    }
  }
}

/**
 * Führt den vollständigen, asynchronen E2E-API-Schnittstellentest aus.
 * 
 * @param {string} adminToken Der JWT-Token des aktuell angemeldeten Administrators
 * @param {function} onProgress Callback zur Übermittlung von Log-Meldungen und Fortschritten
 */
export async function runApiTests(adminToken, onProgress) {
  const log = (message, type = 'info') => {
    onProgress({ message, type });
  };

  // IDs und States, die über die Schritte hinweg gesammelt werden
  let studentId = null;
  let studentEmail = `e2e_student_${Date.now()}@example.com`;
  let studentToken = null;
  
  let tutorId = null;
  let tutorEmail = `e2e_tutor_${Date.now()}@example.com`;
  let tutorToken = null;
  
  let skillId = null;
  let skillName = `E2E-Test-Skill-${Date.now()}`;
  
  let availabilityId = null;
  let anfrageId = null;
  let terminId = null;
  
  const timeConfig = getPastTimeToday();
  const backendUrl = getBackendUrl();
  
  log('==================================================', 'header');
  log('STARTING INTEGRATION & E2E INTERFACE TESTRUN', 'header');
  log(`Timestamp: ${new Date().toLocaleString()}`, 'info');
  log(`Target API Host: ${backendUrl}`, 'info');
  log('==================================================', 'header');

  try {
    // ----------------------------------------------------
    // SCHRITT 1: Schüler registrieren & einloggen (11%)
    // ----------------------------------------------------
    onProgress({ step: 1, percent: 11, stepText: 'Schüler registrieren & einloggen' });
    log('[SCHRITT 1] Registriere Test-Schüler...', 'info');
    
    // 1.1 Registrierung
    const regStudentRes = await fetch(`${backendUrl}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: 'Max E2E-Schüler',
        email: studentEmail,
        password: 'password123',
        roleName: 'student',
        bio: 'Automatisierter Test-Schüler für E2E-Tests'
      })
    });
    
    if (!regStudentRes.ok) {
      throw new Error(await getErrorMessage(regStudentRes, 'Registrierung des Schülers fehlgeschlagen.'));
    }
    log(`[OK] Schüler registriert: ${studentEmail}`, 'success');

    // 1.2 Login
    log('Logge Schüler ein, um Token abzurufen...', 'info');
    const loginStudentRes = await fetch(`${backendUrl}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: studentEmail, password: 'password123' })
    });
    
    if (!loginStudentRes.ok) {
      throw new Error(await getErrorMessage(loginStudentRes, 'Login des Schülers fehlgeschlagen.'));
    }
    const studentAuthData = await loginStudentRes.json();
    studentToken = studentAuthData.token;
    studentId = studentAuthData.id;
    log(`[OK] Schüler eingeloggt (ID: #${studentId})`, 'success');

    // 1.3 Adresse setzen (wichtig für die PDF-Rechnungsgenerierung)
    log('Setze Wohnadresse für Test-Schüler...', 'info');
    const addrStudentRes = await fetch(`${backendUrl}/user/me/adresse`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${studentToken}`
      },
      body: JSON.stringify({
        plz: 44137,
        ort: 'Dortmund',
        strasseHausnummer: 'Musterstraße 42'
      })
    });
    
    if (!addrStudentRes.ok) {
      throw new Error(await getErrorMessage(addrStudentRes, 'Setzen der Schüler-Adresse fehlgeschlagen.'));
    }
    log('[OK] Schüler-Adresse erfolgreich hinterlegt.', 'success');


    // ----------------------------------------------------
    // SCHRITT 2: Tutor registrieren & einloggen (22%)
    // ----------------------------------------------------
    onProgress({ step: 2, percent: 22, stepText: 'Tutor registrieren & einloggen' });
    log('[SCHRITT 2] Registriere Test-Tutor...', 'info');
    
    // 2.1 Registrierung
    const regTutorRes = await fetch(`${backendUrl}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: 'Dr. E2E-Tutor',
        email: tutorEmail,
        password: 'password123',
        roleName: 'dozent',
        bio: 'Automatisierter Test-Tutor für E2E-Tests'
      })
    });
    
    if (!regTutorRes.ok) {
      throw new Error(await getErrorMessage(regTutorRes, 'Registrierung des Tutors fehlgeschlagen.'));
    }
    log(`[OK] Tutor registriert: ${tutorEmail}`, 'success');

    // 2.2 Login
    log('Logge Tutor ein, um Token abzurufen...', 'info');
    const loginTutorRes = await fetch(`${backendUrl}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: tutorEmail, password: 'password123' })
    });
    
    if (!loginTutorRes.ok) {
      throw new Error(await getErrorMessage(loginTutorRes, 'Login des Tutors fehlgeschlagen.'));
    }
    const tutorAuthData = await loginTutorRes.json();
    tutorToken = tutorAuthData.token;
    tutorId = tutorAuthData.id;
    log(`[OK] Tutor eingeloggt (ID: #${tutorId})`, 'success');

    // 2.3 Adresse setzen (wichtig für die PDF-Rechnungsgenerierung)
    log('Setze Wohnadresse für Test-Tutor...', 'info');
    const addrTutorRes = await fetch(`${backendUrl}/user/me/adresse`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tutorToken}`
      },
      body: JSON.stringify({
        plz: 44139,
        ort: 'Dortmund',
        strasseHausnummer: 'Tutor-Allee 100'
      })
    });
    
    if (!addrTutorRes.ok) {
      throw new Error(await getErrorMessage(addrTutorRes, 'Setzen der Tutor-Adresse fehlgeschlagen.'));
    }
    log('[OK] Tutor-Adresse erfolgreich hinterlegt.', 'success');


    // ----------------------------------------------------
    // SCHRITT 3: Termine in Vergangenheit anlegen (33%)
    // ----------------------------------------------------
    onProgress({ step: 3, percent: 33, stepText: 'Verfügbarkeiten in der Vergangenheit anlegen' });
    log('[SCHRITT 3] Erstelle vergangene tutorielle Verfügbarkeit...', 'info');
    log(`Errechneter Timeslot: Heute (${timeConfig.wochentag}), ${timeConfig.startZeit} - ${timeConfig.endeZeit} Uhr (Dauer: ${timeConfig.duration} Min.)`, 'info');
    
    const availRes = await fetch(`${backendUrl}/api/tutors/availability`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tutorToken}`
      },
      body: JSON.stringify({
        wochentag: timeConfig.wochentag,
        startZeit: timeConfig.startZeit,
        endeZeit: timeConfig.endeZeit
      })
    });
    
    if (!availRes.ok) {
      throw new Error(await getErrorMessage(availRes, 'Erstellung der Verfügbarkeit fehlgeschlagen.'));
    }
    log('[OK] Verfügbarkeit erfolgreich im Kalender angelegt.', 'success');

    // Verfügbarkeits-ID abrufen für späteren Cleanup
    log('Rufe Verfügbarkeitsliste des Tutors ab, um ID zu sichern...', 'info');
    const availListRes = await fetch(`${backendUrl}/api/tutors/${tutorId}/availability`, {
      headers: { 'Authorization': `Bearer ${tutorToken}` }
    });
    if (availListRes.ok) {
      const availList = await availListRes.json();
      // Verwende das erste Element, da dieser Tutor neu registriert wurde und nur diese eine Verfügbarkeit besitzt
      const addedAvail = availList[0];
      if (addedAvail) {
        availabilityId = addedAvail.id || addedAvail.originalId;
        log(`[OK] Verfügbarkeits-ID gesichert: #${availabilityId}`, 'success');
      } else {
        log('[WARNUNG] Keine Verfügbarkeit in der Liste gefunden.', 'error');
      }
    }


    // ----------------------------------------------------
    // SCHRITT 4: Skill-Verwaltung (44%)
    // ----------------------------------------------------
    onProgress({ step: 4, percent: 44, stepText: 'Skill erstellen und dem Tutor zuweisen' });
    log('[SCHRITT 4] Erstelle neuen globalen Test-Skill (Admin-Rechte)...', 'info');
    
    // 4.1 Skill erstellen
    const skillCreateRes = await fetch(`${backendUrl}/skill`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${adminToken}`
      },
      body: JSON.stringify({ name: skillName })
    });
    
    if (!skillCreateRes.ok) {
      throw new Error(await getErrorMessage(skillCreateRes, 'Erstellung des Skills fehlgeschlagen.'));
    }
    log(`[OK] Globaler Skill "${skillName}" im Backend angelegt.`, 'success');

    // Skill-ID über GET-Abfrage ermitteln, da der POST-Endpunkt einen leeren Body zurückgibt
    log('Rufe globale Skill-Liste ab, um ID zu sichern...', 'info');
    const skillListRes = await fetch(`${backendUrl}/skill`, {
      headers: { 'Authorization': `Bearer ${adminToken}` }
    });
    if (!skillListRes.ok) {
      throw new Error('Konnte globale Skill-Liste nicht laden, um ID zu sichern.');
    }
    const skillList = await skillListRes.json();
    const addedSkill = skillList.find(s => s.name === skillName);
    if (!addedSkill) {
      throw new Error(`Der erstellte Skill "${skillName}" wurde in der globalen Liste nicht gefunden.`);
    }
    skillId = addedSkill.id;
    log(`[OK] Skill-ID gesichert: #${skillId}`, 'success');

    // 4.2 Tutor den Skill zuweisen
    log(`Weise dem Tutor den neuen Skill #${skillId} zu...`, 'info');
    const skillAssignRes = await fetch(`${backendUrl}/user/me/skills`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tutorToken}`
      },
      body: JSON.stringify({
        skillId: skillId,
        niveau: 'Experte',
        stundenlohn: 20.0
      })
    });
    
    if (!skillAssignRes.ok) {
      throw new Error(await getErrorMessage(skillAssignRes, 'Skill-Zuweisung zum Tutor fehlgeschlagen.'));
    }
    log('[OK] Skill erfolgreich dem Tutor zugewiesen.', 'success');


    // ----------------------------------------------------
    // SCHRITT 5: Nachhilfeanfrage erstellen (55%)
    // ----------------------------------------------------
    onProgress({ step: 5, percent: 55, stepText: 'Nachhilfeanfrage erstellen (Schüler)' });
    
    const requestDateTime = `${timeConfig.dateStr}T${timeConfig.startZeit}:00${timeConfig.offsetStr}`;
    log(`[SCHRITT 5] Schüler sendet Nachhilfeanfrage für: ${requestDateTime}...`, 'info');
    
    const bookingRes = await fetch(`${backendUrl}/api/anfragen`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${studentToken}`
      },
      body: JSON.stringify({
        tutorId: tutorId,
        skillId: skillId,
        datumZeit: requestDateTime,
        dauerMinuten: timeConfig.duration,
        beschreibung: 'Automatisierte E2E Test-Nachhilfe für vergangenen Termin'
      })
    });
    
    if (!bookingRes.ok) {
      throw new Error(await getErrorMessage(bookingRes, 'Erstellung der Nachhilfeanfrage fehlgeschlagen.'));
    }
    log('[OK] Nachhilfeanfrage im Backend eingereicht.', 'success');

    // Anfrage-ID über GET-Abfrage sichern, da der POST-Endpunkt einen leeren Body zurückgibt
    log('Rufe Nachhilfeanfragen des Schülers ab, um ID zu sichern...', 'info');
    const anfragenListRes = await fetch(`${backendUrl}/api/anfragen`, {
      headers: { 'Authorization': `Bearer ${studentToken}` }
    });
    if (!anfragenListRes.ok) {
      throw new Error('Konnte Nachhilfeanfragen des Schülers nicht laden, um ID zu sichern.');
    }
    const anfragenList = await anfragenListRes.json();
    const addedAnfrage = anfragenList.find(a => a.tutor?.id === tutorId) || anfragenList[0];
    if (!addedAnfrage) {
      throw new Error('Die erstellte Nachhilfeanfrage konnte nicht in der Liste gefunden werden.');
    }
    anfrageId = addedAnfrage.id;
    log(`[OK] Anfrage-ID gesichert: #${anfrageId}`, 'success');


    // ----------------------------------------------------
    // SCHRITT 6: Nachhilfeanfrage akzeptieren (66%)
    // ----------------------------------------------------
    onProgress({ step: 6, percent: 66, stepText: 'Nachhilfeanfrage akzeptieren (Tutor)' });
    log(`[SCHRITT 6] Tutor akzeptiert Nachhilfeanfrage #${anfrageId}...`, 'info');
    
    const acceptRes = await fetch(`${backendUrl}/api/anfragen/${anfrageId}/akzeptieren`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${tutorToken}`
      }
    });
    
    if (!acceptRes.ok) {
      throw new Error(await getErrorMessage(acceptRes, 'Akzeptieren der Nachhilfeanfrage fehlgeschlagen.'));
    }
    log('[OK] Nachhilfeanfrage akzeptiert. Lerneinheit (Termin) wurde im Backend generiert.', 'success');

    // Termin-ID abrufen
    log('Rufe Unterrichtstermine ab, um Termin-ID zu ermitteln...', 'info');
    const termineRes = await fetch(`${backendUrl}/termine`, {
      headers: { 'Authorization': `Bearer ${studentToken}` }
    });
    
    if (!termineRes.ok) {
      throw new Error(await getErrorMessage(termineRes, 'Unterrichtstermine konnten nicht ausgelesen werden.'));
    }
    const termineList = await termineRes.json();
    const matchingTermin = termineList.find(t => t.tutor?.id === tutorId || (t.anfrage && t.anfrage.id === anfrageId));
    
    if (!matchingTermin) {
      throw new Error('Der erstellte Termin konnte in der Terminübersicht des Schülers nicht gefunden werden.');
    }
    
    terminId = matchingTermin.id;
    log(`[OK] Zugehörige Termin-ID identifiziert: #${terminId}`, 'success');


    // ----------------------------------------------------
    // SCHRITT 7: Lerneinheit bezahlen & bewerten (77%)
    // ----------------------------------------------------
    onProgress({ step: 7, percent: 77, stepText: 'Lerneinheit bezahlen & bewerten' });
    
    // 7.1 Bezahlen
    log(`[SCHRITT 7] Schüler bezahlt die abgeschlossene Lerneinheit #${terminId}...`, 'info');
    const payRes = await fetch(`${backendUrl}/termine/${terminId}/bezahlen`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${studentToken}`
      }
    });
    
    if (!payRes.ok) {
      throw new Error(await getErrorMessage(payRes, 'Zahlungsabwicklung fehlgeschlagen.'));
    }
    log('[OK] Zahlung erfolgreich vom Backend verbucht.', 'success');

    // 7.2 Bewerten
    log('Schüler bewertet die Lerneinheit mit 5 Sternen...', 'info');
    const rateRes = await fetch(`${backendUrl}/termine/${terminId}/bewertung`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${studentToken}`
      },
      body: JSON.stringify({
        bewertung: 5,
        kommentar: 'Exzellenter E2E-Lernfluss! Sehr zu empfehlen.'
      })
    });
    
    if (!rateRes.ok) {
      throw new Error(await getErrorMessage(rateRes, 'Bewertungsabgabe fehlgeschlagen.'));
    }
    log('[OK] Bewertung erfolgreich eingereicht.', 'success');


    // ----------------------------------------------------
    // SCHRITT 8: PDF-Rechnungs-Check (88%)
    // ----------------------------------------------------
    onProgress({ step: 8, percent: 88, stepText: 'PDF-Rechnung verifizieren' });
    log(`[SCHRITT 8] Fordere PDF-Rechnung für Termin #${terminId} an...`, 'info');
    
    const invoiceRes = await fetch(`${backendUrl}/rechnung/${terminId}`, {
      headers: {
        'Authorization': `Bearer ${studentToken}`
      }
    });
    
    if (!invoiceRes.ok) {
      throw new Error(await getErrorMessage(invoiceRes, 'Laden der PDF-Rechnung schlug fehl.'));
    }
    
    const contentType = invoiceRes.headers.get('Content-Type') || '';
    log(`[OK] Rechnung geladen. HTTP Status: ${invoiceRes.status}`, 'success');
    log(`[OK] Content-Type Header verifiziert: "${contentType}"`, 'success');
    
    if (!contentType.toLowerCase().includes('application/pdf')) {
      log('[WARNUNG] Der Content-Type ist nicht "application/pdf". Prüfung wird dennoch fortgesetzt.', 'info');
    }


    // ----------------------------------------------------
    // SCHRITT 9: Aufräumen (100%)
    // ----------------------------------------------------
    onProgress({ step: 9, percent: 100, stepText: 'System-Bereinigung & Entitäten-Löschung' });
    log('[SCHRITT 9] Starte automatisches Aufräumen...', 'info');
    
    await performCleanup(
      backendUrl, adminToken, tutorToken,
      studentId, tutorId, skillId, skillName, availabilityId,
      log
    );
    
    log('==================================================', 'header');
    log('E2E TEST RUN SUCCESSFULLY COMPLETED!', 'success');
    log('All subsystems are verified and responding correctly.', 'success');
    log('==================================================', 'header');

    return { success: true };

  } catch (error) {
    log('==================================================', 'header');
    log(`❌ TEST RUN ABORTED WITH ERROR: ${error.message}`, 'error');
    log('Running automated emergency cleanup for created entities...', 'info');
    log('==================================================', 'header');

    // Im Fehlerfall trotzdem aufräumen
    try {
      await performCleanup(
        backendUrl, adminToken, tutorToken,
        studentId, tutorId, skillId, skillName, availabilityId,
        log
      );
    } catch (cleanupErr) {
      log(`[WARNUNG] Fehler beim Notfall-Aufräumen: ${cleanupErr.message}`, 'error');
    }

    throw error;
  }
}

/**
 * Hilfsfunktion zum Aufräumen der während des Tests erstellten Testdaten.
 */
async function performCleanup(backendUrl, adminToken, tutorToken, studentId, tutorId, skillId, skillName, availabilityId, log) {
  // 1. Verfügbarkeit löschen
  if (availabilityId && tutorToken) {
    log(`Lösche Test-Verfügbarkeit #${availabilityId} von Tutor...`, 'info');
    const delAvail = await fetch(`${backendUrl}/api/tutors/availability/${availabilityId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${tutorToken}` }
    });
    if (delAvail.ok) {
      log('[CLEANUP OK] Verfügbarkeit gelöscht.', 'success');
    } else {
      log(`[CLEANUP INFO] Verfügbarkeit konnte nicht gelöscht werden (HTTP ${delAvail.status}).`, 'info');
    }
  }

  // 2. Skill-Zuweisung des Tutors entfernen
  if (skillId && tutorToken) {
    log(`Entferne Skill-Zuweisung #${skillId} vom Tutor...`, 'info');
    const delSkillAssign = await fetch(`${backendUrl}/user/me/skills/${skillId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${tutorToken}` }
    });
    if (delSkillAssign.ok) {
      log('[CLEANUP OK] Skill-Zuweisung entfernt.', 'success');
    } else {
      log(`[CLEANUP INFO] Skill-Zuweisung konnte nicht entfernt werden (HTTP ${delSkillAssign.status}).`, 'info');
    }
  }

  // 3. Test-Schüler löschen (Soft-Delete)
  if (studentId) {
    log(`Führe Soft-Delete für Test-Schüler #${studentId} aus...`, 'info');
    const delStudent = await fetch(`${backendUrl}/user/${studentId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${adminToken}` }
    });
    if (delStudent.ok) {
      log('[CLEANUP OK] Schüler erfolgreich deaktiviert (Soft-Delete).', 'success');
    } else {
      log(`[CLEANUP INFO] Schüler Soft-Delete nicht durchgeführt (HTTP ${delStudent.status}). Dies ist ein bekanntes Backend-Verhalten, da die E-Mail 'deletedUser' in der Datenbank nur einmal existieren darf (Unique Constraint) und die erstellten Unterrichtsstunden referenziert sind.`, 'info');
    }
  }

  // 4. Test-Tutor löschen (Soft-Delete)
  if (tutorId) {
    log(`Führe Soft-Delete für Test-Tutor #${tutorId} aus...`, 'info');
    const delTutor = await fetch(`${backendUrl}/user/${tutorId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${adminToken}` }
    });
    if (delTutor.ok) {
      log('[CLEANUP OK] Tutor erfolgreich deaktiviert (Soft-Delete).', 'success');
    } else {
      log(`[CLEANUP INFO] Tutor Soft-Delete nicht durchgeführt (HTTP ${delTutor.status}). Dies ist ein bekanntes Backend-Verhalten, da die E-Mail 'deletedUser' in der Datenbank nur einmal existieren darf (Unique Constraint) und die erstellten Unterrichtsstunden referenziert sind.`, 'info');
    }
  }

  // 5. Globalen Skill archivieren (umbenennen in -deleted)
  if (skillId) {
    log(`Archiviere globalen Test-Skill #${skillId} (Admin)...`, 'info');
    const archivedName = `${skillName}-deleted`;
    const renameSkill = await fetch(`${backendUrl}/skill`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${adminToken}`
      },
      body: JSON.stringify({ id: skillId, name: archivedName })
    });
    if (renameSkill.ok) {
      log(`[CLEANUP OK] Globaler Skill erfolgreich archiviert als "${archivedName}".`, 'success');
    } else {
      log(`[CLEANUP INFO] Globaler Skill #${skillId} konnte nicht archiviert werden (HTTP ${renameSkill.status}).`, 'info');
    }
  }
}
